import { useEffect, useMemo, useRef, useState } from "react"
import { useQuery } from "urql"

import "../graphql/salesOrderQueries"

import {
  GetSalesOrderPricingTotalsDocument,
  type Query,
  type SalesOrderPricingTotalsInput,
  type SalesOrderPricingTotalsLineResult,
  type SalesOrderPricingTotalsRootTotal,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { SalesOrderPricingParams } from "@/screens/CustomerOrders/types"
import { getSalesOrderPricingTotalsTriggerKey, toSalesOrderPricingTotalsInput } from "@/screens/CustomerOrders/utils"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const PRICING_TOTALS_DEBOUNCE_MS = 300

interface AppliedPricingRequest {
  key: string
  input: SalesOrderPricingTotalsInput
}

interface UseCalculateSalesOrderPricingTotalsReturn {
  lines: SalesOrderPricingTotalsLineResult[] | null
  total: SalesOrderPricingTotalsRootTotal | null
  isFetching: boolean
}

export const useCalculateSalesOrderPricingTotals = (
  params: SalesOrderPricingParams
): UseCalculateSalesOrderPricingTotalsReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [applied, setApplied] = useState<AppliedPricingRequest | null>(null)
  const latestParamsRef = useRef(params)

  const triggerKey = getSalesOrderPricingTotalsTriggerKey(params)
  const canPrice = !!params.customerId && params.lines.length > 0
  const isPending = canPrice && triggerKey !== applied?.key

  useEffect(() => {
    latestParamsRef.current = params
  })

  useEffect(() => {
    if (!canPrice || triggerKey === applied?.key) return

    const timeoutId = setTimeout(() => {
      setApplied({ key: triggerKey, input: toSalesOrderPricingTotalsInput(latestParamsRef.current) })
    }, PRICING_TOTALS_DEBOUNCE_MS)

    return () => clearTimeout(timeoutId)
  }, [canPrice, triggerKey, applied?.key])

  const [{ data, fetching, error }] = useQuery<Pick<Query, "salesOrder">>({
    query: GetSalesOrderPricingTotalsDocument,
    variables: { input: applied?.input as SalesOrderPricingTotalsInput },
    pause: !applied,
  })

  const result = data?.salesOrder.calculateSalesOrderPricingTotals

  const totals = useMemo<{
    lines: SalesOrderPricingTotalsLineResult[]
    total: SalesOrderPricingTotalsRootTotal
  } | null>(() => {
    if (result?.__typename === "SalesOrderPricingTotalsSuccess") return { lines: result.lines, total: result.total }
    return null
  }, [result])

  useEffect(() => {
    const message =
      result?.__typename === "SalesOrderPricingTotalsFailure"
        ? result.error.message
        : (error?.graphQLErrors[0]?.message ?? error?.message)

    if (fetching || !message) return

    notify({ type: NotificationType.Error, message })
  }, [result, error, fetching, notify])

  return { lines: totals?.lines ?? null, total: totals?.total ?? null, isFetching: fetching || isPending }
}
